import { Button, Card, Space, Tag, Typography } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";

import request from "@/api";
import { DATETIME_PICKER_FORMAT, DATE_PICKER_FORMAT, DEFAULT_VALUE } from "@/config";
import { dateFormat } from "@/utils";
import { SAND_VERSION_STATE } from "@/meta/index/version";

interface ProductTypeIndexVersionDetail {
    id?: number;
    versionDate?: string;
    publishName?: string;
    updateName?: string;
    updatedTime?: string;
    operateName?: string;
    state?: number;
    remark?: string;
}

interface ProductTypeIndexVersionResponse {
    productTypeIndexVersion?: ProductTypeIndexVersionDetail;
}

/** 复用 Vue 版本详情接口，保持版本详情的历史请求路径。 */
export async function loadIndexVersionInfo(id: number): Promise<ProductTypeIndexVersionResponse> {
    const response = await request.service.get<ProductTypeIndexVersionResponse>(
        `/admin/product-type-index-version/vo/${id}`
    );
    return response.data;
}

export interface IndexVersionInfoPageProps {
    loadVersion?: (id: number) => Promise<ProductTypeIndexVersionResponse>;
}

/** 指数版本详情，保留旧 Vue versionInfo 的字段和驳回备注展示规则。 */
export function IndexVersionInfoPage({ loadVersion = loadIndexVersionInfo }: IndexVersionInfoPageProps) {
    const navigate = useNavigate();
    const { id: rawId } = useParams();
    const id = Number(rawId);
    const [version, setVersion] = useState<ProductTypeIndexVersionDetail | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    useEffect(() => {
        if (!Number.isFinite(id)) {
            setErrorMessage("版本编号无效");
            return;
        }
        let disposed = false;
        void loadVersion(id)
            .then(result => {
                if (!disposed) {
                    setVersion(result.productTypeIndexVersion || null);
                    setErrorMessage(null);
                }
            })
            .catch(() => {
                if (!disposed) setErrorMessage("版本详情加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [id, loadVersion]);

    return (
        <section aria-label="index-version-info-page" className="flex min-h-full flex-col gap-4">
            <Card>
                <Space align="center" className="mb-4">
                    <Typography.Title level={4} className="m-0">
                        版本详情
                    </Typography.Title>
                    {version?.state != null ? <Tag>{SAND_VERSION_STATE.getIntroByCode(version.state)}</Tag> : null}
                </Space>
                {errorMessage ? <Alert role="alert" message={errorMessage} type="error" /> : null}
                {version ? (
                    <dl className="grid grid-cols-2 gap-4">
                        <div>
                            <dt>编号</dt>
                            <dd>{version.id ?? id}</dd>
                        </div>
                        <div>
                            <dt>日期</dt>
                            <dd>{dateFormat(version.versionDate, DATE_PICKER_FORMAT) || DEFAULT_VALUE}</dd>
                        </div>
                        {version.publishName ? (
                            <div>
                                <dt>发布人</dt>
                                <dd>{version.publishName}</dd>
                            </div>
                        ) : null}
                        {version.updateName ? (
                            <div>
                                <dt>编辑人</dt>
                                <dd>{version.updateName}</dd>
                            </div>
                        ) : null}
                        {version.operateName ? (
                            <div>
                                <dt>操作人</dt>
                                <dd>{version.operateName}</dd>
                            </div>
                        ) : null}
                        {version.updatedTime ? (
                            <div>
                                <dt>更新时间</dt>
                                <dd>{dateFormat(version.updatedTime, DATETIME_PICKER_FORMAT)}</dd>
                            </div>
                        ) : null}
                        {version.remark && version.state === SAND_VERSION_STATE.REJECTED.code ? (
                            <div>
                                <dt>备注</dt>
                                <dd>{version.remark}</dd>
                            </div>
                        ) : null}
                    </dl>
                ) : null}
                <Button className="mt-4" onClick={() => navigate(-1)}>
                    返回
                </Button>
            </Card>
        </section>
    );
}

export default IndexVersionInfoPage;
